// ClinicsMobileCards.jsx
import Button from "@components/ui/Button";
import { isPmsConfigured } from "./SchedulingEditor";

function StatusPill({ enabled }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"
      }`}
    >
      {enabled ? "Enabled" : "Disabled"}
    </span>
  );
}

function RatingDisplay({ stats }) {
  if (!stats || stats.total_rated === 0) {
    return <span className="text-gray-400">No ratings yet</span>;
  }

  const score = stats.average_score;
  const getScoreColor = (score) => {
    if (score >= 4) return "bg-green-100 text-green-700";
    if (score >= 3) return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getScoreColor(score)}`}>
        {score.toFixed(1)} / 5
      </span>
      <span className="text-xs text-gray-500">
        ({stats.total_rated} call{stats.total_rated !== 1 ? "s" : ""})
      </span>
      {stats.needs_review > 0 && (
        <span className="text-xs text-orange-600 font-medium">
          {stats.needs_review} need review
        </span>
      )}
    </div>
  );
}

export default function ClinicsMobileCards({ clinics, onEdit, onView, onImpersonate, onRunSetup, setupLoading, onReport }) {
  return (
    <div className="space-y-3">
      {clinics.map((c) => {
        const inactive = c.is_active === false;
        return (
        <div
          key={c.id}
          className={`border rounded-lg p-4 bg-white space-y-3 ${inactive ? "opacity-60" : ""}`}
        >
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <div className="font-semibold text-sm">
                {c.clinic_name || "—"}
              </div>
              {inactive && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  Inactive
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 break-all">{c.id}</div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="text-sm">
              <div className="font-medium text-gray-600 text-xs">Agent</div>
              <div className="break-words">{c.clinic_agent_name || "—"}</div>
            </div>

            <div className="text-sm">
              <div className="font-medium text-gray-600 text-xs">Phone</div>
              <div className="break-words">{c.phone || "—"}</div>
            </div>

            <div className="text-sm">
              <div className="font-medium text-gray-600 text-xs">Timezone</div>
              <div className="break-words">{c.preferred_timezone || "—"}</div>
            </div>

            <div className="text-sm">
              <div className="font-medium text-gray-600 text-xs">Address</div>
              <div className="break-words line-clamp-2">{c.address || "—"}</div>
            </div>
          </div>

          {/* Call Rating */}
          <div className="text-sm">
            <div className="font-medium text-gray-600 text-xs mb-1">Call Rating</div>
            <RatingDisplay stats={c.rating_stats} />
          </div>

          {/* UniFi */}
          <div className="text-sm">
            <div className="font-medium text-gray-600 text-xs mb-1">UniFi</div>
            <div className="flex items-center gap-2">
              <StatusPill enabled={!!c.unifi_scraping_enabled} />
              <div className="text-xs text-gray-700 break-all">
                <span className="font-medium">Console:</span>{" "}
                {c.unifi_console_id || "—"}
              </div>
            </div>
          </div>

          {/* Existing sections */}
          <div className="text-sm">
            <div className="font-medium text-gray-600 text-xs mb-1">
              Clinic Info
            </div>
            <div className="text-gray-700 line-clamp-2">
              {c.clinic_info || "—"}
            </div>
          </div>

          <div className="text-sm">
            <div className="font-medium text-gray-600 text-xs mb-1">
              Welcome Message
            </div>
            <div className="text-gray-700 line-clamp-2">
              {c.welcome_message || "—"}
            </div>
          </div>

          <div className="text-sm">
            <div className="font-medium text-gray-600 text-xs mb-1">
              Agent Questions
            </div>
            <div className="text-gray-700 line-clamp-2 font-mono text-xs">
              {c.agent_questions ? JSON.stringify(c.agent_questions) : "—"}
            </div>
          </div>

          <div className="flex gap-2 pt-2 flex-wrap">
            <Button
              color="light"
              size="sm"
              className="flex-1"
              onClick={() =>
                onView(`${c.clinic_name || "Clinic"} Info`, c.clinic_info)
              }
            >
              View Info
            </Button>

            <Button
              color="light"
              size="sm"
              className="flex-1"
              onClick={() =>
                onView(
                  `${c.clinic_name || "Clinic"} Welcome Message`,
                  c.welcome_message
                )
              }
            >
              View Welcome
            </Button>

            <Button size="sm" className="flex-1" onClick={() => onEdit(c)}>
              Edit
            </Button>
            <Button
              size="sm"
              color="purple"
              className="flex-1"
              onClick={() => onReport(c)}
            >
              Report
            </Button>
            {c.owner_user_id && (
              <Button
                size="sm"
                color="amber"
                className="flex-1"
                onClick={() => onImpersonate(c)}
              >
                Login As
              </Button>
            )}
            {isPmsConfigured(c.active_pms_provider, c.pms_config) && (
              <Button
                size="sm"
                color="green"
                className="flex-1"
                disabled={setupLoading}
                onClick={() => onRunSetup(c.id)}
              >
                {setupLoading ? "Setting up…" : "Run Setup"}
              </Button>
            )}
          </div>
        </div>
        );
      })}

      {clinics.length === 0 && (
        <div className="py-6 text-center text-gray-500">
          No clinics match your search.
        </div>
      )}
    </div>
  );
}
